import React, { FC, useMemo } from 'react';
import { useTheme } from 'styled-components';
import {
  GroupElem,
  RectElem,
  SVGElem,
  TextElem,
} from './IndividualReportGroupsDistribution.styles';
import { EScoreRating } from '../../../services/individualReport';

const GAP = 4;
const INSET = 0.5;

const calculateWidth = (value: number, gaps: number) =>
  Math.round(((512 - gaps * GAP - 2 * INSET) * value) / 10) / 10;

interface IIndividualReportGroupsDistribution {
  distribution: number[];
  score_rating: EScoreRating;
  code: string;
}

const IndividualReportGroupsDistribution: FC<IIndividualReportGroupsDistribution> = ({
  distribution,
  score_rating,
  code,
}) => {
  const theme = useTheme();
  const INVERTED_CODES = new Set(['C9', 'T12', 'T9']);

  const shouldInvert = (code: string) => INVERTED_CODES.has(code);

  const baseColors: Record<EScoreRating, string[][]> = {
    [EScoreRating.WEAK]: [
      [theme.colors.accent.red[100], theme.colors.accent.red[10]],
      [theme.colors.accent.blue[30], theme.colors.accent.blue[100]],
      [theme.colors.accent.green[30], theme.colors.accent.green[100]],
    ],
    [EScoreRating.NORMAL]: [
      [theme.colors.accent.red[30], theme.colors.accent.red[100]],
      [theme.colors.accent.blue[100], theme.colors.accent.blue[10]],
      [theme.colors.accent.green[30], theme.colors.accent.green[100]],
    ],
    [EScoreRating.STRONG]: [
      [theme.colors.accent.red[30], theme.colors.accent.red[100]],
      [theme.colors.accent.blue[30], theme.colors.accent.blue[100]],
      [theme.colors.accent.green[100], theme.colors.accent.green[10]],
    ],
  };

  const normalizedDistribution = useMemo(() => {
    if (!shouldInvert(code)) return distribution;

    return [distribution[2], distribution[1], distribution[0]];
  }, [distribution, code]);

  const getColorIndex = (index: number) => {
    if (!shouldInvert(code)) return index;

    if (index === 0) return 2;
    if (index === 2) return 0;
    return 1;
  };

  const rects = useMemo(
    () =>
      normalizedDistribution.map((value, index, array) => {
        const gaps = array.length - array.filter(value => value === 0).length - 1;
        const width = calculateWidth(value, gaps);

        return {
          x: array.reduce((acc, _, idx) => {
            const DX = gaps > 0 && array[idx] > 0 ? GAP : 0;

            return idx < index ? acc + calculateWidth(array[idx], gaps) + DX : acc;
          }, INSET),
          width,
          value,
        };
      }),
    [normalizedDistribution]
  );

  return (
    <SVGElem xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 24">
      {rects.map(({ x, width, value }, index) => {
        const colorIndex = getColorIndex(index);
        const [background, text] = baseColors[score_rating][colorIndex];

        const TEXT_INSET = value < 10 ? 4 : 8;

        return (
          <GroupElem key={index}>
            <RectElem y="0" height="24" rx="3.5" fill={background} {...{ x, width }} />
            {value >= 5 ? (
              <TextElem x={x + (width / 2 - (GAP + TEXT_INSET))} y="16.5" fill={text}>
                {`${value}%`}
              </TextElem>
            ) : null}
          </GroupElem>
        );
      })}
    </SVGElem>
  );
};

export default IndividualReportGroupsDistribution;
